import React from 'react';
import useEmailVerifier from 'hooks/useEmailVerifier';
import CustomDialog from 'components/CustomDialog';
import { RootState } from '../../store';
import { Link, Typography } from '@mui/material';
import ErrorMessage from '../ErrorMessage';
import { clearEmailValidation, saveFirstPassword } from '../../store/slices/user';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { useHistory } from 'react-router-dom';
import SetFirstPasswordForm from '../SetFirstPasswordForm';

const EmailVerification = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const user = useAppSelector((state: RootState) => state.user);
    const auth = useAppSelector((state: RootState) => state.auth);
    const {
        validatingEmail,
        validatingEmailSuccess,
        validatingEmailError,
        validatingEmailErrorCont,
        savingPassword,
        savingPasswordError,
        savingPasswordErrorCont,
        savingPasswordSuccess,
    } = user;
    const isComplete = auth.tokenData?.data?.isComplete;

    useEmailVerifier();

    const handleClose = () => {
        dispatch(clearEmailValidation());
        history.push('/');
    };

    const shouldShowPasswordForm = validatingEmailSuccess && !savingPasswordSuccess && !isComplete;

    return (
        <CustomDialog
            title={'Account activation'}
            processing={validatingEmail || savingPassword}
            success={validatingEmailSuccess && savingPasswordSuccess}
            error={validatingEmailError || savingPasswordError}
            processingCopy={validatingEmail ? 'Email Validation in Progress...' : 'Password change in Progress...'}
            onClose={handleClose}
            successContent={
                <>
                    <Typography variant="subtitle1">Awesome! Your password's all set! 🥳</Typography>
                    <Typography variant="body1">
                        Go ahead and&nbsp;
                        <Link href="/edit-profile" underline="hover">
                            jump into your account
                        </Link>
                        . And hey, don't forget to fill out your profile a bit more – it'll help other pilots find you
                        more easily!
                    </Typography>
                </>
            }
            errorContent={
                <ErrorMessage
                    specificError={
                        validatingEmailErrorCont ??
                        savingPasswordErrorCont ??
                        'Oops! We encountered an unexpected issue.'
                    }
                />
            }
        >
            {shouldShowPasswordForm && (
                <>
                    <Typography variant="body2" gutterBottom>
                        Your email has been verified! Now set a password to complete your registration:
                    </Typography>
                    <SetFirstPasswordForm
                        onSubmit={(password) => void dispatch(saveFirstPassword({ password }))}
                        isProcessing={savingPassword}
                        errorMessage={savingPasswordErrorCont}
                        submitLabel="Set your password"
                    />
                </>
            )}
        </CustomDialog>
    );
};

export default EmailVerification;
